import { useEffect, useRef, useState } from 'react'
import {
  Loader2, AlertTriangle, Upload, Trash2, Image as ImageIcon, FolderDown,
  X, ChevronLeft, ChevronRight, LineChart, RefreshCw, BadgeCheck, Hourglass, Stamp, DownloadCloud,
} from 'lucide-react'
import { CARD } from '../components/ui.jsx'
import { useConfirm } from '../components/ConfirmProvider.jsx'
import { api } from '../lib/api.js'
import { resizeImageFile } from '../lib/imageResize.js'
import { OperationSummary, CstSatisfactionPanel, OralHealthPanel, aggregateCst } from '../components/ReportContent.jsx'

/**
 * Report tab — event-photo gallery.
 *
 * Staff uploads multiple photos (up to 50 per batch). Each file is resized
 * client-side to 1080px on the long edge and re-encoded as JPEG before being
 * shipped to the API one-at-a-time (small concurrency window). Photos are
 * shown as a thumbnail grid with delete + click-to-view; the same gallery is
 * surfaced to HR via the public report token.
 */

const MAX_BATCH = 50

export default function ReportTab({ op }) {
  const opId = op?.id
  const confirm = useConfirm()
  const fileInputRef = useRef(null)
  const [photos, setPhotos] = useState(null)        // null = loading
  const [err, setErr] = useState(null)
  const [busy, setBusy] = useState(false)
  const [uploadState, setUploadState] = useState(null)
  // { total, done, failed, currentName } while a batch is uploading
  const [lightbox, setLightbox] = useState(null)    // { index }
  const [dragOver, setDragOver] = useState(false)
  // Bumped after an approve so the report preview re-imports from Xano.
  const [dataSignal, setDataSignal] = useState(0)

  async function reload() {
    if (!opId) return
    setErr(null)
    try {
      const { photos } = await api.operations.reportPhotos.list(opId)
      setPhotos(photos || [])
    } catch (e) { setErr(extractErr(e)); setPhotos([]) }
  }
  useEffect(() => { reload() /* eslint-disable-next-line */ }, [opId])

  async function uploadFiles(rawFiles) {
    // accept="image/*" gates the picker; trust any file with size > 0.
    // iOS often returns file.type='' and file.name='' for HEIC/live photos,
    // so type/extension checks would incorrectly drop them.
    const files = [...(rawFiles || [])].filter((f) => f && f.size > 0)
    if (files.length === 0) {
      setErr('กรุณาเลือกไฟล์ภาพ (JPG / PNG / HEIC)')
      return
    }
    if (files.length > MAX_BATCH) {
      setErr(`เลือกได้สูงสุด ${MAX_BATCH} ภาพต่อครั้ง (เลือกมา ${files.length})`)
      return
    }
    setErr(null)
    setBusy(true)
    setUploadState({ total: files.length, done: 0, failed: 0, currentName: files[0]?.name || '' })

    const uploaded = []
    let failed = 0
    for (let i = 0; i < files.length; i++) {
      const f = files[i]
      setUploadState((s) => ({ ...s, currentName: f.name, done: i, failed }))
      try {
        const { dataUri, width, height, filename } = await resizeImageFile(f)
        const res = await api.operations.reportPhotos.upload(opId, {
          data: dataUri, filename, width, height,
        })
        if (res?.photo) uploaded.push(res.photo)
        else failed += 1
      } catch (e) {
        failed += 1
        console.warn('[report-photo] upload failed:', f.name, e?.message)
      }
    }

    setUploadState({ total: files.length, done: files.length, failed, currentName: '' })
    // Refresh from the server so we always show the canonical sort order +
    // presigned URLs (data-uri previews would only last one paint anyway).
    await reload()
    setBusy(false)
    if (failed > 0) setErr(`อัปโหลดไม่สำเร็จ ${failed}/${files.length} ภาพ`)
    setTimeout(() => setUploadState(null), failed > 0 ? 5000 : 1800)
  }

  async function deletePhoto(p) {
    const ok = await confirm({
      title:        'ลบภาพ',
      message:      'ภาพนี้จะถูกลบและไม่สามารถกู้คืนได้',
      confirmLabel: 'ลบ',
      tone:         'danger',
    })
    if (!ok) return
    try {
      await api.operations.reportPhotos.remove(opId, p.id)
      setPhotos((curr) => (curr || []).filter((x) => x.id !== p.id))
    } catch (e) { setErr(extractErr(e)) }
  }

  function onPick() { fileInputRef.current?.click() }
  async function onFileChange(e) {
    const files = [...(e.target.files || [])]  // snapshot before iOS clears the live FileList
    if (e.target) e.target.value = ''
    await uploadFiles(files)
  }
  function onDragOver(e)  { e.preventDefault(); if (!busy) setDragOver(true) }
  function onDragLeave(e) { e.preventDefault(); setDragOver(false) }
  async function onDrop(e) {
    e.preventDefault(); setDragOver(false)
    if (busy) return
    await uploadFiles(e.dataTransfer?.files)
  }

  const list = photos || []
  const total = list.length
  const loading = photos === null

  return (
    <div className="space-y-4">
      <ApprovalCard op={op} onAfterApprove={() => setDataSignal((s) => s + 1)} />

    <div onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
      className={CARD + ' p-4 md:p-6 ' + (dragOver ? 'ring-2 ring-amber-400 bg-amber-50/40' : '')}>

      {/* Header */}
      <div className="mb-3 md:mb-4">
        <div className="flex items-center justify-between gap-2 mb-1">
          <h2 className="text-[15.5px] md:text-[18px] font-semibold text-zinc-900 inline-flex items-center gap-2">
            <LineChart size={16} className="text-amber-500" /> ภาพประกอบรายงาน
          </h2>
          <div className="shrink-0 flex items-center gap-2">
            <button type="button" onClick={reload} disabled={busy} title="รีเฟรช"
              className="inline-flex items-center justify-center w-9 h-9 rounded-md ring-1 ring-zinc-200 bg-white hover:bg-zinc-50 text-zinc-600 disabled:opacity-50">
              <RefreshCw size={14} />
            </button>
            {total > 0 && (
              <a href={api.operations.reportPhotos.zipUrl(opId)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md ring-1 ring-zinc-300 bg-white hover:bg-zinc-50 text-zinc-700 text-[13.5px] font-medium">
                <FolderDown size={13} />
                <span className="hidden sm:inline">ดาวน์โหลด zip</span>
              </a>
            )}
            <button type="button" onClick={onPick} disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[13.5px] font-semibold shadow-sm disabled:opacity-50">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
              เพิ่มภาพ
            </button>
          </div>
        </div>
        <p className="text-[12px] md:text-[13px] text-zinc-500 leading-snug">
          อัปโหลดภาพจากหน้างาน — แสดงในรายงานของ HR และดาวน์โหลดเป็น zip ได้
          <span className="text-zinc-400"> · สูงสุด {MAX_BATCH} ภาพต่อครั้ง · ย่ออัตโนมัติ ≤ 1080px</span>
        </p>
      </div>

      <input ref={fileInputRef} type="file" multiple
        accept="image/*"
        className="hidden" onChange={onFileChange} />

      {/* Progress / errors */}
      {uploadState && uploadState.total > 0 && (
        <div className="mb-3 p-2.5 rounded-md bg-amber-50 ring-1 ring-amber-200 text-amber-800 text-[13px]">
          <div className="flex items-center gap-2">
            {uploadState.done < uploadState.total
              ? <Loader2 size={13} className="animate-spin" />
              : (uploadState.failed > 0 ? <AlertTriangle size={13} /> : <ImageIcon size={13} />)}
            <span className="font-medium tabular-nums">{uploadState.done}/{uploadState.total}</span>
            <span className="truncate text-[12.5px] text-amber-700/80">{uploadState.currentName || ''}</span>
            {uploadState.failed > 0 && (
              <span className="ml-auto text-rose-700 font-medium">ผิดพลาด {uploadState.failed}</span>
            )}
          </div>
          <div className="mt-1.5 h-1.5 rounded-full bg-amber-100 overflow-hidden">
            <div className="h-full bg-amber-500 transition-all"
              style={{ width: `${Math.round((uploadState.done / uploadState.total) * 100)}%` }} />
          </div>
        </div>
      )}
      {err && (
        <div className="mb-3 px-3 py-2 rounded-md bg-rose-50 ring-1 ring-rose-200 text-rose-700 text-[13px] inline-flex items-center gap-1.5">
          <AlertTriangle size={12} /> {err}
        </div>
      )}

      {/* Count line */}
      {!loading && (
        <p className="text-[12.5px] text-zinc-500 mb-2.5">
          {total === 0 ? 'ยังไม่มีภาพในรายงานนี้' : `ทั้งหมด ${total.toLocaleString()} ภาพ`}
        </p>
      )}

      {/* Grid */}
      {loading ? (
        <div className="py-16 text-center">
          <Loader2 className="animate-spin text-amber-400 mx-auto" />
        </div>
      ) : total === 0 ? (
        <EmptyDropZone onPick={onPick} dragOver={dragOver} />
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 md:gap-3">
          {list.map((p, i) => (
            <PhotoCard key={p.id} photo={p}
              onClick={() => setLightbox({ index: i })}
              onDelete={() => deletePhoto(p)}
              disabled={busy} />
          ))}
          {/* Trailing tile — quick re-pick from inside the grid */}
          <button type="button" onClick={onPick} disabled={busy} title="เพิ่มภาพ"
            className={'aspect-square rounded-lg border-2 border-dashed flex flex-col items-center justify-center transition disabled:opacity-50 ' +
              (dragOver
                ? 'border-amber-500 bg-amber-50 text-amber-700'
                : 'border-amber-300 hover:border-amber-500 hover:bg-amber-50 text-amber-700')}>
            <Upload size={18} />
            <span className="text-[10.5px] font-medium mt-0.5">เพิ่มภาพ</span>
          </button>
        </div>
      )}

      {lightbox && (
        <Lightbox photos={list} index={lightbox.index}
          onClose={() => setLightbox(null)}
          onChange={(idx) => setLightbox({ index: idx })} />
      )}
    </div>

      {/* Same summary + satisfaction block HR sees in their report — so staff
          can preview it without opening the public link. */}
      <ReportPreview op={op} importSignal={dataSignal} />
    </div>
  )
}


/* ─────────────────────── report preview (staff) ─────────────────────── */

// Renders the operation summary + แบบประเมินความพึงพอใจ exactly as the HR
// portal shows it. The patient count is a SNAPSHOT cached on the operation —
// we do NOT hit Xano on load. Staff presses "รีเฟรชข้อมูล" (or approves the
// report) to pull a fresh count from Xano; that value is what HR sees.
// CST scores/comments stay live from D1 (cheap, and staff can hide/delete).
function parseOral(v) {
  if (!v) return null
  if (typeof v === 'object') return v
  try { return JSON.parse(v) } catch { return null }
}

function ReportPreview({ op, importSignal }) {
  const [rows, setRows]         = useState(null)
  const [treated, setTreated]   = useState(op?.treated_patients ?? null)
  const [treatedAt, setTreatedAt] = useState(op?.treated_patients_at ?? null)
  const [oral, setOral]         = useState(() => parseOral(op?.oral_health))
  const [refreshing, setRefreshing] = useState(false)
  const [err, setErr]           = useState(null)

  // CST is D1-only — cheap, always live.
  useEffect(() => {
    let cancelled = false
    api.operations.surveys.cstResponses(op.id)
      .then((cstData) => { if (!cancelled) setRows(cstData?.rows || []) })
      .catch((e) => { if (!cancelled) setErr(extractErr(e)) })
    return () => { cancelled = true }
  }, [op.id])

  // Auto-import after an approve (parent bumps importSignal). Skip first render
  // so we don't hit Xano just from opening the tab.
  const firstSignal = useRef(true)
  useEffect(() => {
    if (firstSignal.current) { firstSignal.current = false; return }
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importSignal])

  // The "นำเข้าข้อมูล" button (and approve) pull BOTH Xano-derived snapshots.
  async function refresh() {
    setRefreshing(true); setErr(null)
    try {
      const [t, o] = await Promise.all([
        api.operations.treatedCount(op.id).catch((e) => { throw e }),
        api.operations.oralHealth(op.id).catch((e) => { throw e }),
      ])
      if (t?.count != null) { setTreated(t.count); setTreatedAt(t.computed_at || Math.floor(Date.now() / 1000)) }
      if (o) setOral(o)
    } catch (e) { setErr(extractErr(e)) }
    finally { setRefreshing(false) }
  }

  const cst = aggregateCst(rows || [])
  const patients = treated ?? 0

  return (
    <div className={CARD + ' p-4 md:p-6'}>
      <div className="flex items-start justify-between gap-2 mb-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-amber-700 mb-0.5">รายงานที่ HR จะเห็น</div>
          <h2 className="text-[15.5px] md:text-[18px] font-semibold text-zinc-900 inline-flex items-center gap-2">
            <LineChart size={16} className="text-amber-500" /> สรุปผลการดำเนินงาน
          </h2>
        </div>
        <button type="button" onClick={refresh} disabled={refreshing}
          title="นำเข้าข้อมูลล่าสุดจาก Xano"
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-md ring-1 ring-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 text-[13px] font-medium disabled:opacity-60">
          {refreshing ? <Loader2 size={13} className="animate-spin" /> : <DownloadCloud size={13} />}
          นำเข้าข้อมูล
        </button>
      </div>

      {treated == null && !refreshing && (
        <div className="mb-3 px-3 py-2 rounded-md bg-amber-50 ring-1 ring-amber-200 text-amber-800 text-[12.5px] inline-flex items-center gap-1.5">
          <AlertTriangle size={12} /> ยังไม่ได้นำเข้าข้อมูล — กด "นำเข้าข้อมูล" หรือกดอนุมัติรายงาน
        </div>
      )}
      {err && (
        <div className="mb-3 px-3 py-2 rounded-md bg-rose-50 ring-1 ring-rose-200 text-rose-700 text-[13px] inline-flex items-center gap-1.5">
          <AlertTriangle size={12} /> {err}
        </div>
      )}

      <OperationSummary o={op} patients={patients} asOf={treatedAt} />

      {oral && oral.examined > 0 && (
        <div className="mb-6">
          <OralHealthPanel data={oral} />
        </div>
      )}

      {rows === null ? (
        <div className="py-8 text-center text-zinc-400"><Loader2 className="inline w-4 h-4 animate-spin mr-1.5" /> กำลังโหลดคะแนน…</div>
      ) : cst.count > 0 ? (
        <CstSatisfactionPanel cst={cst} />
      ) : (
        <p className="text-[13.5px] text-zinc-400 italic">ยังไม่มีผลแบบประเมินความพึงพอใจสำหรับ operation นี้</p>
      )}
    </div>
  )
}


/* ─────────────────────── approval card ─────────────────────── */

function ApprovalCard({ op, onAfterApprove }) {
  const [approved, setApproved] = useState(!!op?.report_approved)
  const [approvedAt, setApprovedAt] = useState(op?.report_approved_at || null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)

  async function toggle() {
    const next = !approved
    setBusy(true); setErr(null)
    try {
      await api.operations.update(op.id, { report_approved: next })
      setApproved(next)
      setApprovedAt(next ? Math.floor(Date.now() / 1000) : null)
      // On approve, trigger the report preview to import + snapshot fresh Xano
      // data so HR sees up-to-date numbers the moment the report goes live.
      if (next) onAfterApprove?.()
    } catch (e) { setErr(extractErr(e)) }
    finally { setBusy(false) }
  }

  return (
    <div className={CARD + ' p-4 md:p-5'}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-amber-700 mb-0.5">การอนุมัติรายงานเพื่อส่งให้ HR</div>
          <h3 className="text-[14.5px] md:text-[15px] font-semibold text-zinc-900 inline-flex items-center gap-2">
            {approved
              ? <><BadgeCheck size={15} className="text-emerald-600" /> รายงานได้รับการอนุมัติแล้ว</>
              : <><Hourglass size={15} className="text-amber-500" /> รายงานยังไม่ได้รับการอนุมัติ</>}
          </h3>
          <p className="mt-1 text-[12px] md:text-[12.5px] text-zinc-500 leading-snug max-w-[640px]">
            {approved
              ? <>HR เห็นภาพประกอบรายงานและคะแนน CST ในรายงานของพวกเขาแล้ว{approvedAt ? ` · อนุมัติเมื่อ ${fmtThaiDateTime(approvedAt)}` : ''}</>
              : 'ภาพประกอบรายงานและคะแนน CST จะถูกซ่อนจากรายงาน HR จนกว่าเจ้าหน้าที่ Tooth Taxi จะกดอนุมัติ'}
          </p>
          {err && (
            <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-rose-50 ring-1 ring-rose-200 text-rose-700 text-[11.5px]">
              <AlertTriangle size={11} /> {err}
            </div>
          )}
        </div>
        <button onClick={toggle} disabled={busy}
          className={'shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[13px] font-semibold transition disabled:opacity-60 ' +
            (approved
              ? 'ring-1 ring-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
              : 'bg-amber-500 hover:bg-amber-600 text-white shadow-sm shadow-amber-500/20')}>
          {busy ? <Loader2 size={13} className="animate-spin" />
            : approved ? <BadgeCheck size={13} /> : <Stamp size={13} />}
          {approved ? 'ยกเลิกอนุมัติ' : 'อนุมัติเพื่อส่งให้ HR'}
        </button>
      </div>
    </div>
  )
}

function fmtThaiDateTime(unixSeconds) {
  if (!unixSeconds) return ''
  const d = new Date(unixSeconds * 1000)
  return d.toLocaleString('th-TH-u-ca-gregory', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Bangkok',
  })
}


/* ─────────────────────── thumbnail tile ─────────────────────── */

function PhotoCard({ photo: p, onClick, onDelete, disabled }) {
  const [errored, setErrored] = useState(false)
  return (
    <div className="group relative aspect-square">
      <button type="button" onClick={onClick} title={p.filename || 'photo'}
        className="block w-full h-full rounded-lg overflow-hidden ring-1 ring-zinc-200 bg-zinc-100 hover:ring-amber-400 hover:shadow-sm transition">
        {p.url && !errored ? (
          <img src={p.url} alt={p.filename} loading="lazy"
            className="w-full h-full object-cover"
            onError={() => setErrored(true)} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-400">
            <ImageIcon size={20} />
          </div>
        )}
      </button>
      <button onClick={(e) => { e.stopPropagation(); onDelete() }} disabled={disabled}
        title="ลบ" aria-label="ลบภาพ"
        className="absolute top-1 right-1 w-6 h-6 rounded-full bg-rose-500 hover:bg-rose-600 text-white flex items-center justify-center shadow opacity-0 group-hover:opacity-100 focus:opacity-100 transition disabled:opacity-50">
        <Trash2 size={11} />
      </button>
    </div>
  )
}


/* ─────────────────────── empty drop zone ─────────────────────── */

function EmptyDropZone({ onPick, dragOver }) {
  return (
    <button type="button" onClick={onPick}
      className={'w-full rounded-2xl border-2 border-dashed p-10 text-center transition ' +
        (dragOver
          ? 'border-amber-500 bg-amber-50 text-amber-700'
          : 'border-zinc-200 hover:border-amber-400 hover:bg-amber-50/50 text-zinc-500')}>
      <Upload size={26} className="mx-auto mb-2" />
      <p className="text-[14px] font-medium text-zinc-700">คลิกเพื่อเลือกภาพ หรือ ลากไฟล์มาวางที่นี่</p>
      <p className="text-[12.5px] text-zinc-400 mt-1">รองรับ JPG / PNG / HEIC · สูงสุด {MAX_BATCH} ภาพต่อครั้ง</p>
    </button>
  )
}


/* ─────────────────────── lightbox ─────────────────────── */

function Lightbox({ photos, index, onClose, onChange }) {
  const p = photos[index]
  const prev = () => onChange((index - 1 + photos.length) % photos.length)
  const next = () => onChange((index + 1) % photos.length)

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape')     onClose()
      else if (e.key === 'ArrowLeft')  prev()
      else if (e.key === 'ArrowRight') next()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, photos.length])

  if (!p) return null

  return (
    <div className="fixed inset-0 z-[80] flex flex-col bg-zinc-900/90 backdrop-blur-sm">
      <header className="flex items-center gap-2 px-3 md:px-4 py-2.5 bg-zinc-900/60 text-white">
        <span className="text-[13.5px] truncate flex-1 font-medium" title={p.filename}>
          {p.filename || 'photo'} <span className="text-white/50">· {index + 1}/{photos.length}</span>
        </span>
        {p.download_url && (
          <a href={p.download_url} download
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[13px] bg-white/10 hover:bg-white/20 transition">
            ดาวน์โหลด
          </a>
        )}
        <button onClick={onClose} className="w-8 h-8 rounded-md inline-flex items-center justify-center hover:bg-white/15">
          <X size={18} />
        </button>
      </header>
      <div className="flex-1 relative flex items-center justify-center p-3 md:p-6" onClick={onClose}>
        {photos.length > 1 && (
          <button onClick={(e) => { e.stopPropagation(); prev() }}
            className="absolute left-2 md:left-4 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center">
            <ChevronLeft size={22} />
          </button>
        )}
        <img src={p.url} alt={p.filename}
          onClick={(e) => e.stopPropagation()}
          className="max-w-full max-h-full object-contain rounded-md shadow-2xl" />
        {photos.length > 1 && (
          <button onClick={(e) => { e.stopPropagation(); next() }}
            className="absolute right-2 md:right-4 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center">
            <ChevronRight size={22} />
          </button>
        )}
      </div>
    </div>
  )
}


function extractErr(e) {
  const m = (e?.message || '').match(/:\s*\{?"?error"?"?:\s*"?([^"}\n]+)/)
  return m ? m[1] : (e?.message || 'error')
}
